import React from "react";
import { Link } from "react-router-dom";
import { Inbox } from "lucide-react";
import { Button } from "../ui/button";
import { titleCase } from "../../lib/api";

export function PageHeader({ title, description, actions }) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl" data-testid="page-title">{title}</h1>
        {description && <p className="mt-1.5 text-sm text-ink-soft">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, icon: Icon, tone = "brand" }) {
  const tones = { brand: "bg-brand-50 text-brand", graphite: "bg-graphite-50 text-graphite", ink: "bg-surface-200 text-ink" };
  return (
    <div className="kt-card p-5" data-testid={`stat-${String(label).toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <p className="text-sm font-medium text-ink-soft">{label}</p>
        {Icon && <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${tones[tone]}`}><Icon className="h-4.5 w-4.5" /></span>}
      </div>
      <p className="mt-3 text-2xl font-bold tracking-tight text-ink">{value}</p>
      {hint && <p className="mt-1 text-xs text-ink-muted">{hint}</p>}
    </div>
  );
}

const STATUS_TONES = {
  // rides
  searching: "bg-surface-200 text-ink-soft",
  driver_assigned: "bg-graphite-50 text-graphite",
  driver_arriving: "bg-graphite-50 text-graphite",
  in_progress: "bg-brand-50 text-brand",
  completed: "bg-graphite text-white",
  cancelled: "bg-red-50 text-red-700",
  // orders
  placed: "bg-surface-200 text-ink-soft",
  no_partner: "bg-amber-50 text-amber-800",
  accepted: "bg-graphite-50 text-graphite",
  preparing: "bg-brand-50 text-brand",
  ready: "bg-brand-100 text-brand-700",
  out_for_delivery: "bg-graphite-100 text-graphite-700",
  delivered: "bg-graphite text-white",
  // kyc
  unverified: "bg-surface-200 text-ink-soft",
  pending: "bg-brand-50 text-brand",
  verified: "bg-graphite text-white",
  rejected: "bg-red-50 text-red-700",
  approved: "bg-graphite text-white",
  none: "bg-surface-200 text-ink-soft",
  active: "bg-graphite text-white",
  paused: "bg-surface-200 text-ink-soft",
  // payments
  paid: "bg-graphite-50 text-graphite",
  // generic
  online: "bg-graphite text-white",
  offline: "bg-surface-200 text-ink-soft",
  published: "bg-graphite-50 text-graphite",
  draft: "bg-surface-200 text-ink-soft",
};

const STATUS_LABELS = {
  placed: "Awaiting confirmation",
  finding_partner: "Finding partner",
  no_partner: "No delivery partner",
  out_for_delivery: "Out for delivery",
};

export function StatusBadge({ status, className = "" }) {
  const tone = STATUS_TONES[status] || "bg-surface-200 text-ink-soft";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${tone} ${className}`} data-testid={`status-${status}`}>
      {STATUS_LABELS[status] || titleCase(status || "unknown")}
    </span>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, text, action }) {
  return (
    <div className="kt-card flex flex-col items-center px-6 py-14 text-center" data-testid="empty-state">
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-200 text-ink-muted"><Icon className="h-7 w-7" /></span>
      <h3 className="mt-5 text-lg font-semibold text-ink">{title}</h3>
      {text && <p className="mt-2 max-w-sm text-sm text-ink-soft">{text}</p>}
      {action && (
        <Button asChild className="mt-6 rounded-full bg-brand text-white hover:bg-brand-600">
          <Link to={action.to}>{action.label}</Link>
        </Button>
      )}
    </div>
  );
}

export function LoadingBlock({ rows = 3, className = "" }) {
  return (
    <div className={`space-y-3 ${className}`} data-testid="loading">
      {Array.from({ length: rows }).map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-surface-200" />)}
    </div>
  );
}

export function Stepper({ steps, current }) {
  const idx = steps.findIndex((s) => s.key === current);
  return (
    <ol className="flex items-center gap-2" data-testid="stepper">
      {steps.map((s, i) => {
        const done = i <= idx;
        return (
          <li key={s.key} className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${done ? "bg-graphite text-white" : "bg-surface-200 text-ink-muted"}`}>{i + 1}</span>
            <span className={`hidden min-w-0 truncate text-[11px] font-medium sm:block ${done ? "text-ink" : "text-ink-muted"}`} title={s.label}>{s.label}</span>
            {i < steps.length - 1 && <span className={`h-0.5 min-w-[6px] flex-1 rounded ${i < idx ? "bg-graphite" : "bg-surface-300"}`} />}
          </li>
        );
      })}
    </ol>
  );
}

export function Section({ title, description, children, actions, className = "" }) {
  return (
    <section className={`kt-card p-6 ${className}`}>
      {(title || actions) && (
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            {title && <h2 className="text-lg font-semibold text-ink">{title}</h2>}
            {description && <p className="mt-1 text-sm text-ink-soft">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}
